import { useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FaArrowLeftLong } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import {
  LOGIN_ROUTE,
  resetPassword,
  sendOTP,
  sendOtpToUsername,
  showToast,
  SIGN_UP_ROUTE,
  useAppDispatch,
  useAppSelector,
} from "@hypexone/uicore";
import Button from "../../components/shared/Button/Button";
import { useSignUpModal } from "../../context/SignUpModalContext";
import { parseRecoveredUsername } from "../../../utils/forgotPassword/parseRecoveredUsername";
import CopyIcon from "../../../assets/icons/user-menu-icons/copy-icon.svg";
import { isWhatsappSignup } from "../../../constants/Branding";

interface ForgotPasswordModalProps {
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ onClose }) => {
  const [method, setMethod] = useState("Username");
  const [username, setUsername] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpTimer, setOtpTimer] = useState<number | undefined>(undefined);
  const [uName, setUName] = useState("");

  const { otpSent } = useAppSelector((state) => state.auth);
  const { langData } = useAppSelector((state) => state.cms);
  const { domainConfig } = useAppSelector((state) => state.common);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setSignUpModalOpen } = useSignUpModal();

  const handleOtpTimer = (time: number) => {
    if (time >= 0) {
      setOtpTimer(time);
      setTimeout(() => handleOtpTimer(time - 1), 1000);
    } else {
      setTimeout(() => setOtpTimer(undefined), 1000);
    }
  };

  useEffect(() => {
    if (otpSent) {
      handleOtpTimer(60);
    }
  }, [otpSent]);

  const showConfirmPasswordClickHandler = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const showPasswordClickHandler = () => {
    setShowPassword(!showPassword);
  };

  const handleMethodChange = (newMethod: string) => {
    setMethod(newMethod);
    setUsername("");
    setMobile("");
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handleMobileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMobile(event.target.value);
  };

  const handleCountryCodeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryCode(event.target.value);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    dispatch(
      sendOtpToUsername({
        username,
        phonenumber: countryCode.slice(1) + mobile,
        option: method,
      })
    );
  };

  // HA signup off + signin on => Sign Up opens the registration WhatsApp link instead of the sign-up page.
  const registrationWhatsappLink = domainConfig?.suppportContacts?.find(
    (ct) => ct.contactType === "REGISTRATION_WHATSAPP_LINK"
  )?.details;
  const whatsappSignup = isWhatsappSignup(domainConfig);
  const showSignUp = whatsappSignup
    ? !!registrationWhatsappLink
    : domainConfig?.signup && domainConfig?.b2cEnabled;

  const redirectToSignUp = () => {
    if (whatsappSignup) {
      window.open(registrationWhatsappLink, "_blank");
      return;
    }
    onClose();
    // Avoid stacking Sign Up over the login page
    if (isMobile || pathname === LOGIN_ROUTE) {
      navigate(SIGN_UP_ROUTE);
      return;
    }
    setSignUpModalOpen(true);
  };

  const handleCopyUsername = () => {
    navigator.clipboard.writeText(uName).then(() => {
      dispatch(
        showToast({
          message:
            langData?.["username_copied_txt"] ||
            langData?.["text_copied_txt"] ||
            "Username copied!",
          type: "success",
        })
      );
    });
  };

  const handleResetPassword = () => {
    if (newPassword !== confirmPassword) {
      dispatch(
        showToast({
          message: "Passwords didn't match",
          type: "error",
        })
      );
      return;
    }
    dispatch(
      resetPassword({
        username,
        phoneNumber: "91" + mobile,
        resetPassword: newPassword,
        otp,
        option: method,
      })
    )
      .unwrap()
      .then((data) => {
        if (method === "Username" && username) {
          onClose();
          return;
        }
        const recoveredUsername = parseRecoveredUsername(data);
        if (recoveredUsername) {
          setUName(recoveredUsername);
        } else {
          onClose();
        }
      })
      .catch(() => {
        // Toast already shown by uicore thunk
      });
  };

  const { otpSuccess } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (otpSuccess) {
      handleOtpTimer(60);
    }
  }, [otpSuccess]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex items-center justify-center w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-xl shadow-xl bg-goldenYellow p-6 relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="forgot-password-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-10 h-10 flex items-center justify-center bg-black text-white rounded-full hover:opacity-90"
          aria-label="Close"
        >
          <IoClose className="text-white text-2xl" />
        </button>

        <div className="w-full">
          <Button
            onClick={onClose}
            className="flex items-center gap-1.5 text-sm font-medium whitespace-nowrap w-[61px] text-white"
          >
            <FaArrowLeftLong className="text-black" />
            <span className="my-auto text-black">{langData?.["back"]}</span>
          </Button>
          <h2
            id="forgot-password-modal-title"
            className="text-2xl text-center font-bold mb-4 text-black mt-2"
          >
            {uName
              ? langData?.["forgot_username_password_txt"] ||
                "Forgot Username/Password?"
              : langData?.["forgot_password"]}
          </h2>

          {uName ? (
            <div className="flex flex-col items-center py-4">
              <div className="flex items-center justify-center gap-2.5 flex-wrap">
                <span className="text-black text-sm whitespace-nowrap">
                  {langData?.["your_username_is_txt"] ||
                    langData?.["your_username_is"] ||
                    "Your Username is:"}
                </span>
                <span className="text-black text-lg font-bold whitespace-nowrap">
                  {uName}
                </span>
                <button
                  type="button"
                  onClick={handleCopyUsername}
                  className="p-1 shrink-0 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
                  aria-label="Copy username"
                >
                  <img
                    src={CopyIcon}
                    alt="copy username"
                    className="w-5 h-5 cursor-pointer"
                  />
                </button>
              </div>
            </div>
          ) : !otpSuccess ? (
            <>
              <p className="mb-6 text-white">{langData?.["otp_send_msg"]}</p>
              <div className="mb-4 flex">
                <Button
                  className={`flex-1 py-2 ${
                    method === "Username" ? "bg-black1 text-white" : "bg-gray-200"
                  }`}
                  onClick={() => handleMethodChange("Username")}
                >
                  {langData?.["username"]}
                </Button>
                <Button
                  className={`flex-1 py-2 ${
                    method === "mobile" ? "bg-black1 text-white" : "bg-gray-200"
                  }`}
                  onClick={() => handleMethodChange("mobile")}
                >
                  Mobile Number
                </Button>
              </div>
              <form onSubmit={handleSubmit}>
                {method === "Username" ? (
                  <div className="mb-4">
                    <label className="block text-black">{langData?.["username"]}</label>
                    <input
                      type="text"
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      value={username}
                      onChange={handleUsernameChange}
                      placeholder={langData?.["enter_username_txt"]}
                    />
                  </div>
                ) : (
                  <div className="mb-4">
                    <label className="block text-gray-700">
                      {langData?.["mobile_number"]}
                    </label>
                    <div className="flex">
                      <select
                        className="mt-1 block w-20 px-3 py-2 bg-white border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                      >
                        <option value="+91">+91</option>
                      </select>
                      <input
                        type="text"
                        className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={mobile}
                        onChange={handleMobileChange}
                        placeholder={langData?.["enter_mobile_no"]}
                      />
                    </div>
                  </div>
                )}
                <Button
                  type="submit"
                  className="w-full bg-black1 text-goldenYellow font-semibold py-2 rounded-md hover:bg-black focus:outline-none focus:text-white"
                >
                  {langData?.["send_otp"]}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-gray-700">{langData?.["otp"]}</label>
                <input
                  type="number"
                  className="appearance-none mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-700 focus:border-blue-800 sm:text-sm"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder={langData?.["enter_otp_txt"]}
                />
              </div>
              <div className="mt-5">
                <label htmlFor="fp-password" className="self-start text-neutral-500">
                  {langData?.["password"]}
                </label>
                <div className="flex gap-5 justify-between px-3.5 py-3 mt-3 bg-white rounded-lg border border-solid border-neutral-200 placeholder:text-neutral-500 text-opacity-60">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="fp-password"
                    className="w-full bg-transparent border-none focus:outline-none"
                    placeholder={langData?.["enter_your_password"]}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <div
                    className="cursor-pointer text-neutral-500"
                    onClick={showPasswordClickHandler}
                  >
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </div>
                </div>
              </div>
              <div className="mt-5 mb-5">
                <label htmlFor="fp-confirm-password" className="self-start text-neutral-500">
                  {langData?.["confirm_password"]}
                </label>
                <div className="flex gap-5 justify-between px-3.5 py-3 mt-3 bg-white rounded-lg border border-solid border-neutral-200 placeholder:text-neutral-500 text-opacity-60">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="fp-confirm-password"
                    className="w-full bg-transparent border-none focus:outline-none"
                    placeholder={langData?.["confirm_your_password"]}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <div
                    className="cursor-pointer text-neutral-500"
                    onClick={showConfirmPasswordClickHandler}
                  >
                    {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                  </div>
                </div>
              </div>
              <Button
                className={`w-full my-1 py-2 rounded-md focus:outline-none ${
                  otpTimer !== undefined && otpTimer !== null && otpTimer >= 0
                    ? "bg-neutral-400 text-white cursor-not-allowed"
                    : "bg-blue-500 hover:bg-blue-600 text-white cursor-pointer"
                }`}
                disabled={
                  otpTimer !== undefined && otpTimer !== null && otpTimer >= 0
                }
                onClick={() => {
                  if (!otpTimer) {
                    dispatch(sendOTP("91" + mobile));
                    handleOtpTimer(60);
                  }
                }}
              >
                {otpTimer !== undefined && otpTimer !== null && otpTimer >= 0
                  ? `Resend in ${otpTimer} sec`
                  : "Send OTP"}
              </Button>
              <Button
                className="w-full bg-blue-800 text-white py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:bg-blue-800 mt-1"
                onClick={handleResetPassword}
              >
                {langData?.["reset_password_txt"]}
              </Button>
            </>
          )}
          {showSignUp && (
            <p className="self-center mt-4 text-sm text-center text-gray-600">
              {langData?.["account_not_found_txt"]}{" "}
              <button
                type="button"
                className="font-semibold text-gray-900 underline bg-transparent border-none cursor-pointer p-0"
                onClick={redirectToSignUp}
              >
                {whatsappSignup ? langData?.["sign_in"] || "Sign In" : langData?.["sign_up"]}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
